import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { defaultTooltipConfig, defaultGridConfig, defaultLegendConfig, defaultScaleTitleConfig } from '../../utils/chartConfig';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const WARNING = '#ffc107';
const INFO = '#599BE3';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const PortfolioBreakdownChart = React.forwardRef(({ portfolioIncome, balance, yieldRate, totalReturn, years, age1 }, ref) => {
  const labels = years.map((y) => `${y}`);

  const { yieldIncome, capitalGainsDrawdown, principalDrawdown } = useMemo(() => {
    const totalReturnRate = (totalReturn || 0) * 100;
    const portfolioYieldRate = (yieldRate || 0) * 100;

    const yieldInc = years.map((_, i) => {
      const bal = balance[i] || 0;
      return Math.max(0, bal * (portfolioYieldRate / 100));
    });

    const capGainsDrawdown = [];
    const prinDrawdown = [];

    years.forEach((_, i) => {
      const income = portfolioIncome[i] || 0;
      const yld = yieldInc[i] || 0;
      const totalDrawdown = Math.max(0, income - yld);
      const bal = balance[i] || 0;
      const capitalGainAvailable = Math.max(0, bal * ((totalReturnRate - portfolioYieldRate) / 100));
      const capitalGainsUsed = Math.min(capitalGainAvailable, totalDrawdown);
      const principalUsed = Math.max(0, totalDrawdown - capitalGainsUsed);

      capGainsDrawdown.push(capitalGainsUsed);
      prinDrawdown.push(principalUsed);
    });

    return { yieldIncome: yieldInc, capitalGainsDrawdown: capGainsDrawdown, principalDrawdown: prinDrawdown };
  }, [portfolioIncome, balance, yieldRate, totalReturn, years]);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Portfolio Yield',
        data: yieldIncome,
        backgroundColor: WARNING + 'CC',
        borderColor: WARNING,
        borderWidth: 1,
        stack: 'main',
      },
      {
        label: 'Principal Drawdown (funded from capital gains)',
        data: capitalGainsDrawdown,
        backgroundColor: INFO + 'CC',
        borderColor: INFO,
        borderWidth: 1,
        stack: 'main',
      },
      {
        label: 'Principal Drawdown (funded from existing balance)',
        data: principalDrawdown,
        backgroundColor: INFO + '66',
        borderColor: INFO,
        borderWidth: 1,
        stack: 'main',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Portfolio Income and Capital Return Breakdown (50th Percentile)',
        font: { size: 14 },
      },
      legend: defaultLegendConfig,
      tooltip: {
        ...defaultTooltipConfig,
        callbacks: {
          title: (items) => {
            const idx = items[0]?.dataIndex;
            const year = years[idx];
            const age = age1 != null ? age1 + year : null;
            return age != null ? `Year ${year} (Age ${age})` : `Year ${year}`;
          },
          label: (item) => {
            return `${item.dataset.label}: ${formatCurrency(item.raw)}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Age',
        },
        ticks: {
          callback: function (value, index) {
            const year = years[index];
            if (age1 != null) {
              return `${age1 + year}`;
            }
            return `${year}`;
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Annual Amount ($)',
        },
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  return (
    <div style={{ height: '350px', position: 'relative' }}>
      <Bar ref={ref} data={chartData} options={options} />
    </div>
  );
});

PortfolioBreakdownChart.displayName = 'PortfolioBreakdownChart';

export default PortfolioBreakdownChart;
